import React from 'react';
import { Button } from '../ui/button';
import {
  CheckCircle,
  XCircle,
  Monitor,
  Eye,
  Gamepad2,
  Plug,
  SlidersHorizontal,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import VersionSelect from './VersionSelect';

// Helper to format relative time (e.g. "3d ago", "12h ago")
function formatRelativeTime(timestampMs) {
  if (!timestampMs) return null;
  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function parseOtaStatus(otaStatus) {
  if (otaStatus === undefined || otaStatus === null) return '';
  if (typeof otaStatus !== 'string') return String(otaStatus);
  try {
    const obj = JSON.parse(otaStatus);
    const status = obj.status;
    const version = obj.version;
    return (
      <>
        {status && <div className="text-xs">{status}</div>}
        {version && <div className="text-xs">{version}</div>}
      </>
    );
  } catch (e) {
    return otaStatus;
  }
}

// WiFi signal strength bars icon (1-4 bars based on RSSI dBm)
function WifiSignalBars({ rssiDbm }) {
  let bars;
  let color;
  if (rssiDbm > -50) {
    bars = 4; color = '#22c55e';
  } else if (rssiDbm > -60) {
    bars = 3; color = '#22c55e';
  } else if (rssiDbm > -70) {
    bars = 2; color = '#f97316';
  } else {
    bars = 1; color = '#ef4444';
  }

  const barWidth = 2.5;
  const gap = 1.5;
  const heights = [4, 7, 10, 13];
  const totalWidth = 4 * barWidth + 3 * gap;

  return (
    <svg
      width={totalWidth}
      height={14}
      viewBox={`0 0 ${totalWidth} 14`}
      className="flex-shrink-0"
      title={`WiFi RSSI: ${Math.round(rssiDbm)} dBm`}
    >
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * (barWidth + gap)}
          y={14 - h}
          width={barWidth}
          height={h}
          rx={0.5}
          fill={i < bars ? color : '#d1d5db'}
        />
      ))}
    </svg>
  );
}

function DeviceRow({
  device,
  availableAppVersions,
  availableOsVersions,
  onTargetVersionChange,
  onInstantUpdateChange,
  onTargetOsVersionChange,
  onInstantOsUpdateChange,
  onViewInstantLog,
  onViewHotplug,
  onViewOverrideSettings,
  onViewRemoteManagement,
  onViewLog,
  onRequestLogDump,
  onViewCrashLog,
  onViewDocument,
  hasHotplug,
  hasOverrides,
  rssi,
  firstSigninTime,
}) {
  const isPlaceholder = device.hasDeviceManagement === false;
  const pingPeriodSeconds = 30;
  const pingBufferSeconds = 5;
  const onlinePingDate = !isPlaceholder ? device.onlinePing?.toDate() : null;
  const isOnline = onlinePingDate && (Date.now() - onlinePingDate.getTime() < (pingPeriodSeconds + pingBufferSeconds) * 1000);

  // Resolve current app version value for the select
  const appVersionValue = device.targetAppVersion == null
    ? ''
    : (availableAppVersions.some(doc => parseInt(doc.id, 10) === device.targetAppVersion)
       ? device.targetAppVersion.toString()
       : 'invalid');

  const osVersionValue = device.targetOsVersion == null
    ? ''
    : (availableOsVersions.some(doc => parseInt(doc.id, 10) === device.targetOsVersion)
       ? device.targetOsVersion.toString()
       : 'invalid');

  return (
    <tr className={`border-b last:border-b-0 hover:bg-muted/30 ${isPlaceholder ? 'opacity-60' : ''}`}>
      {/* Family name + online status */}
      <td className="px-3 py-2 w-[140px]">
        <div className="flex items-center gap-2">
          {isPlaceholder ? (
            <Monitor className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          ) : isOnline ? (
            <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          )}
          <div className="min-w-0 overflow-hidden">
            <div className="flex items-center gap-1">
              <span className="truncate text-blue-600 hover:underline cursor-pointer" onClick={() => onViewDocument({ type: 'family', id: device.id })}>{device.familyName}</span>
              {isOnline && rssi != null && (
                <WifiSignalBars rssiDbm={rssi} />
              )}
            </div>
            <span className="text-[10px] text-muted-foreground block truncate hover:text-blue-600 hover:underline cursor-pointer" title={device.id} onClick={() => onViewDocument({ type: 'family', id: device.id })}>{device.id}</span>
            {isPlaceholder && (
              <span className="text-[10px] text-muted-foreground block">Not signed in</span>
            )}
          </div>
        </div>
      </td>

      {/* Signed in time */}
      <td className="px-3 py-2 text-center text-xs w-[70px]">
        {firstSigninTime
          ? <span className="text-muted-foreground">{formatRelativeTime(firstSigninTime)}</span>
          : <span className="text-muted-foreground">—</span>}
      </td>

      {/* Current app version */}
      <td className="px-3 py-2 text-center font-mono text-xs w-[60px]">
        {isPlaceholder ? <span className="text-muted-foreground">-</span> : (device.currentAppVersion || '-')}
      </td>

      {/* Target app version select */}
      <td className="px-3 py-2 text-center w-[160px]">
        <div className="space-y-1">
          {isPlaceholder ? (
            <span className="text-xs text-muted-foreground">-</span>
          ) : (
            <>
              <VersionSelect
                versions={availableAppVersions}
                value={appVersionValue}
                onValueChange={(val) => onTargetVersionChange(device.id, val)}
                placeholder="Select"
              />
              <div className="flex items-center justify-end gap-1 text-xs">
                <span>Inst:</span>
                <input
                  type="checkbox"
                  checked={device.instantUpdate || false}
                  onChange={(e) => onInstantUpdateChange(device.id, e.target.checked)}
                  className="w-3 h-3"
                />
              </div>
            </>
          )}
        </div>
      </td>

      {/* OTA Status */}
      <td className="px-3 py-2 text-xs font-mono w-[120px]">
        {isPlaceholder ? (
          <span className="text-muted-foreground">-</span>
        ) : (
          <div className="truncate">
            {parseOtaStatus(device.otaStatus)}
          </div>
        )}
      </td>

      {/* Log button */}
      <td className="px-3 py-2 text-center w-[50px]">
        {isPlaceholder ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewInstantLog(device)}
            className="h-6 w-6 p-0"
          >
            <Eye className="w-3 h-3" />
          </Button>
        )}
      </td>

      {/* Settings / Hotplug button */}
      <td className="px-3 py-2 text-center w-[50px]">
        {isPlaceholder ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewHotplug(device)}
            disabled={!hasHotplug}
            className="h-6 w-6 p-0"
            title={hasHotplug ? 'Hotplug settings' : 'No hotplug settings (device offline or none declared)'}
          >
            <Plug className="w-3 h-3" />
          </Button>
        )}
      </td>

      {/* Override settings button */}
      <td className="px-3 py-2 text-center w-[50px]">
        {isPlaceholder ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <Button
            variant={hasOverrides ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewOverrideSettings(device)}
            className="h-6 w-6 p-0"
            title={hasOverrides ? 'Override settings (active)' : 'Override settings'}
          >
            <SlidersHorizontal className="w-3 h-3" />
          </Button>
        )}
      </td>

      {/* Remote management button */}
      <td className="px-3 py-2 text-center w-[50px]">
        {isPlaceholder ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewRemoteManagement(device)}
            className="h-6 w-6 p-0"
          >
            <Gamepad2 className="w-3 h-3" />
          </Button>
        )}
      </td>

      {/* Dump / uploaded log */}
      <td className="px-3 py-2 text-center w-[100px]">
        {isPlaceholder ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <div className="space-y-1">
            {/* Timestamp of last available log */}
            {device.logDumpTimestamp ? (
              <div className="text-xs text-muted-foreground">
                {(() => {
                  const ts = device.logDumpTimestamp.toDate ? device.logDumpTimestamp.toDate() : new Date(device.logDumpTimestamp);
                  const ageInMinutes = Math.floor((Date.now() - ts.getTime()) / 60000);
                  return ageInMinutes >= 60
                    ? `${Math.floor(ageInMinutes / 60)}h ago`
                    : `${ageInMinutes}m ago`;
                })()}
              </div>
            ) : device.lastUploadedLog ? (
              <div className="text-xs text-muted-foreground">
                {(() => {
                  const ageInMinutes = Math.floor((Date.now() - device.lastUploadedLog.toDate().getTime()) / 60000);
                  return ageInMinutes >= 60
                    ? `${Math.floor(ageInMinutes / 60)}h ago`
                    : `${ageInMinutes}m ago`;
                })()}
              </div>
            ) : null}
            <div className="flex items-center justify-center gap-1">
              {/* Request latest log button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRequestLogDump(device.id)}
                disabled={device.logDumpStatus === 'uploading'}
                className="h-6 w-6 p-0"
                title="Request latest log from device"
              >
                <RefreshCw className={`w-3 h-3 ${device.logDumpStatus === 'uploading' ? 'animate-spin' : ''}`} />
              </Button>
              {/* View log button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewLog(device.id, device.familyName, device.logDumpPath)}
                disabled={!device.logDumpPath && !device.lastUploadedLog || device.logDumpStatus === 'uploading'}
                className="h-6 w-6 p-0"
                title="View log"
              >
                <Eye className="w-3 h-3" />
              </Button>
              {/* Crash log indicator */}
              {device.crashLogPath && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewCrashLog(device.id, device.familyName, device.crashLogPath)}
                  className="h-6 w-6 p-0 border-orange-300 text-orange-500"
                  title="View crash log"
                >
                  <AlertTriangle className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        )}
      </td>

      {/* Current OS version */}
      <td className="px-3 py-2 text-center font-mono text-xs w-[60px]">
        {isPlaceholder ? <span className="text-muted-foreground">-</span> : (device.currentOsVersion || '-')}
      </td>

      {/* Target OS version select */}
      <td className="px-3 py-2 text-center w-[160px]">
        <div className="space-y-1">
          {isPlaceholder ? (
            <span className="text-xs text-muted-foreground">-</span>
          ) : (
            <>
              <VersionSelect
                versions={availableOsVersions}
                value={osVersionValue}
                onValueChange={(val) => onTargetOsVersionChange(device.id, val)}
                placeholder="Select"
              />
              <div className="flex items-center justify-end gap-1 text-xs">
                <span>Inst:</span>
                <input
                  type="checkbox"
                  checked={device.instantOsUpdate || false}
                  onChange={(e) => onInstantOsUpdateChange(device.id, e.target.checked)}
                  className="w-3 h-3"
                />
              </div>
            </>
          )}
        </div>
      </td>

      {/* OS OTA Status */}
      <td className="px-3 py-2 text-xs font-mono w-[120px]">
        {isPlaceholder ? (
          <span className="text-muted-foreground">-</span>
        ) : (
          <div className="truncate">
            {parseOtaStatus(device.osOtaStatus)}
          </div>
        )}
      </td>
    </tr>
  );
}

export default React.memo(DeviceRow);
